import React, { createContext, useContext, useState, useEffect } from 'react';

const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState('en'); // Default to English

  useEffect(() => {
    // Function to detect device language
    const detectDeviceLanguage = () => {
      // Check if language is saved in localStorage first
      const savedLanguage = localStorage.getItem('selectedLanguage');
      if (savedLanguage && ['pl', 'en', 'fr'].includes(savedLanguage)) {
        return savedLanguage;
      }

      // Detect device language from navigator.language
      const deviceLanguage = navigator.language || navigator.userLanguage;
      
      if (deviceLanguage) {
        // Extract language code (e.g., 'en-US' -> 'en', 'fr-FR' -> 'fr')
        const langCode = deviceLanguage.split('-')[0].toLowerCase();
        
        // Map to supported languages
        if (langCode === 'pl') return 'pl';
        if (langCode === 'fr') return 'fr';
        if (langCode === 'en') return 'en';
        
        // For other languages, try to detect from full language string
        if (deviceLanguage.includes('polish') || deviceLanguage.includes('polski')) return 'pl';
        if (deviceLanguage.includes('french') || deviceLanguage.includes('français')) return 'fr';
        if (deviceLanguage.includes('english') || deviceLanguage.includes('anglais')) return 'en';
      }

      // Default to English for unsupported languages
      return 'en';
    };

    const detectedLanguage = detectDeviceLanguage();
    setLanguage(detectedLanguage);
    
    // Save the detected language to localStorage if not already saved
    if (!localStorage.getItem('selectedLanguage')) {
      localStorage.setItem('selectedLanguage', detectedLanguage);
    }
  }, []);

  const value = {
    language,
    setLanguage,
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export { LanguageContext };
